import type { ReactElement } from 'react';

/**
 * Kubit foundation icons, vendored as inline SVG.
 *
 * The named-icon path (`icon="check-circle"`) needs Kubit's asset provider — a
 * `baseHost` plus a theme whose `ICONS_STYLES` maps the name to a hosted file —
 * and the default `KubitProvider` wires up neither, nor does the bundled theme
 * even carry that mapping. Rather than reconstruct that whole stack for two
 * glyphs, we take the exact art Kubit ships (`icon_check_circle`, `icon_block`)
 * and render it through the element slot that `onIcon` / `offIcon` accept.
 *
 * The only change from the shipped files is `fill="#1A1A1A"` -> `currentColor`,
 * so each half of the toggle colours its own glyph.
 */

const SIZE = 14;

/** Kubit `check-circle`: a tick inside a ring. Used for the "on" state. */
export function CheckCircleIcon(): ReactElement {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM12 20C7.589 20 4 16.411 4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 16.411 16.411 20 12 20Z"
        fill="currentColor"
      />
      <path
        d="M9.99811 13.587L7.69911 11.292L6.28711 12.708L10.0001 16.413L16.7061 9.70703L15.2921 8.29303L9.99811 13.587Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * The site's mark, served from `public/`.
 *
 * A plain `<img>` rather than Kubit's `Image`, which wraps its own in a
 * `<figure><picture>` built for responsive art direction — overhead with
 * nothing to buy at 28px inside a button. `alt` carries the accessible name,
 * so the break card announces "Break" instead of an emoji.
 */
export function LogoMark({ size = 28 }: { size?: number }): ReactElement {
  return <img src="/logo.svg" alt="Break" width={size} height={size} />;
}

/**
 * A plain chevron, drawn here rather than vendored: Kubit's accordion rotates
 * whatever it is handed by 180° when the panel opens, so the glyph only has to
 * point down and be symmetrical about its vertical axis.
 */
export function ChevronDownIcon(): ReactElement {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41Z" fill="currentColor" />
    </svg>
  );
}

/** Kubit `block`: a slashed ring. Used for the "off" state. */
export function BlockIcon(): ReactElement {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM4 12C4 10.154 4.634 8.458 5.688 7.103L16.897 18.312C15.543 19.366 13.846 20 12 20C7.589 20 4 16.411 4 12ZM18.312 16.897L7.103 5.688C8.458 4.634 10.154 4 12 4C16.411 4 20 7.589 20 12C20 13.846 19.365 15.542 18.312 16.897Z"
        fill="currentColor"
      />
    </svg>
  );
}
